import { GameState } from "src/components/implementationTypes";
import playerDisplayColors from "../../playerColors";
import { TableContext, TableContextType } from "../tableContext";
import { ReactNode, useCallback, useContext, useMemo } from "react";
import { PlayerEvent } from "src/components/types";
import "./GameStatusBar.css";
import { fixMalformattedMessage } from "./utility";

type GameStatusBarProps = {
  statusBarStatus: string;
  addGameIconsToText: (text: string, options?: { [key: string]: any }) => ReactNode;
  reconnectAttempt?: number;
};

export function GameStatusBar({ statusBarStatus, addGameIconsToText, reconnectAttempt = 0 }: GameStatusBarProps) {
  let {
    eventManager,
    tableState,
    adjustedGameState,
    playerColors,
    playAsColor,
    makeTurnsAs,
    editMode,
    skipAnimation,
    selectedColor,
    selectedComponent,
  } = useContext(TableContext as React.Context<TableContextType>);

  let activeState = useMemo<GameState>(
    () => adjustedGameState(tableState.getActiveState()),
    [tableState.getActiveState(), playAsColor, selectedColor, selectedComponent], // really not sure whether this is good practice, but it works
  );

  if (playAsColor === "alwaysactive") {
    playAsColor = activeState.activePlayer;
  }
  let wakeup = false;
  let activeStatePlayer = activeState?.activePlayer;
  let activeColor = activeStatePlayer;
  if (
    playAsColor === activeStatePlayer ||
    playAsColor === "alwaysactive" ||
    (Array.isArray(activeColor) && activeColor.includes(playAsColor))
  ) {
    wakeup = true;
  }
  let state = activeState;
  let statusName = state.name;

  const stateButtonClickCallBack = useCallback(
    (name: string, editModeButton: boolean = false) => {
      console.log("clicked", name);
      eventManager.newEvent("game move", {
        sourcePlayer: makeTurnsAs,
        buttonName: name,
        editModeButton,
        type: "press state button",
      } as PlayerEvent);
    },
    [eventManager, playAsColor],
  );

  function formatMessage(messageText?: string) {
    const fallbackText = "No instructions. " + (wakeup ? "your" : activeColor + "'s") + " turn";
    return <>{parseTextWithMarkup(messageText ?? fallbackText)}</>;
  }

  function parseTextWithMarkup(text: string): ReactNode[] {
    const parts: ReactNode[] = [];
    const regex = /(\{[^}]+\}|\[[^\]]+\])/g;
    let lastIndex = 0;

    for (const match of text.matchAll(regex)) {
      const matchText = match[0];
      const index = match.index!;

      if (index > lastIndex) {
        parts.push(text.slice(lastIndex, index));
      }

      if (matchText.startsWith("{")) {
        const variableKey = matchText.slice(1, -1);
        let className = "";
        let style: React.CSSProperties = {};
        let content: ReactNode = variableKey;

        switch (variableKey) {
          case "activePlayer":
            className = "state-player-name";
            style = {
              background: playerDisplayColors[activeColor ?? ""] ?? "transparent",
              padding: "3px",
              whiteSpace: "pre-wrap",
              borderRadius: "4px",
            };
            content = wakeup
              ? "You"
              : (playerColors?.find((c) => c.color === activeColor)?.userInfo?.username ?? activeColor);
            break;
          case "their":
            className = "pronoun";
            content = wakeup ? "your" : "their";
            break;
          case "is":
            className = "verb";
            content = wakeup ? "are" : "is";
            break;
          default:
            if (typeof state[variableKey] === "string") {
              content = parseTextWithMarkup(state[variableKey]);
            } else if (state[variableKey]) {
              content = state[variableKey];
            }
            break;
        }

        parts.push(
          <span className={className} style={style} key={index}>
            {content}
          </span>,
        );
      } else if (matchText.startsWith("[")) {
        parts.push(addGameIconsToText(matchText));
      }

      lastIndex = index + matchText.length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  }

  function createUndo() {
    eventManager.newEvent("undo", {
      sourcePlayer: makeTurnsAs ?? "",
      type: "undo",
    });
  }

  function createButtonList() {
    let result = [
      <button
        key={"button-undo"}
        onClick={createUndo}
        disabled={activeColor === "everyone" && !editMode ? true : false}
      >
        Undo
      </button>,
    ];

    if (Array.isArray(state.buttons)) {
      let resultButtons = [...state.buttons];
      if (editMode) {
        resultButtons = resultButtons
          .concat(
            window.componentPool.__defaultEditButtons?.map((button: { [key: string]: any }) => {
              return { ...button, editModeButton: true };
            }) ?? [],
          )
          .concat(state.editModeButtons ?? []);
      }

      for (let button of resultButtons) {
        result.push(
          <button
            name={button.name}
            key={"button-" + button.name}
            onClick={() => {
              eventManager.newClick();
              stateButtonClickCallBack.bind(null, button.name, button.editModeButton)();
            }}
            style={button.style}
            disabled={button.disabled}
          >
            {addGameIconsToText(button.text)}
          </button>,
        );
      }
    }
    return result;
  }

  function createStatusInner() {
    if (statusBarStatus === "disconnected") {
      return (
        <div className="disconnected-status">
          <span className="connection-spinner"></span>
          <span className="status-message">
            Connection lost — reconnecting{reconnectAttempt > 0 ? ` (attempt ${reconnectAttempt})` : "..."}
          </span>
        </div>
      );
    }

    if (statusBarStatus === "waitingResponse") {
      return (
        <>
          <div className="status-message-wrap">
            <span className="status-message">Move recorded, please wait...</span>
          </div>
          <div className="button-wrap"></div>
        </>
      );
    }

    if (statusBarStatus === "waitingEvaluate") {
      return (
        <>
          <div className="status-message-wrap">
            <span className="status-message">Updating game situation, please wait...</span>
          </div>
          <div className="button-wrap">
            <button name={"skip"} onClick={skipAnimation}>
              Skip
            </button>
          </div>
        </>
      );
    }
    if (statusBarStatus === "outOfSync") {
      return (
        <>
          <span className="status-message error">
            You are out of sync with the server. Please, refresh the page. If the problem persists, report this issue.
          </span>
        </>
      );
    }

    let displayButtons = wakeup || activeColor == "rival" || editMode || activeColor === "everyone";
    let isDev = localStorage.getItem("server-env") === "dev";
    isDev = false;
    return (
      <>
        <div className="status-message-wrap">
          <span className="status-message">{formatMessage(state.message)}</span>
          {isDev ? <span className="state-name">({statusName})</span> : null}
        </div>
        {displayButtons ? <div className="button-wrap">{createButtonList()}</div> : <></>}
      </>
    );
  }

  const isDisconnected = statusBarStatus === "disconnected";
  return (
    <div className={`game-status-bar ${wakeup ? "wakeup" : ""} ${isDisconnected ? "disconnected" : ""}`}>
      {createStatusInner()}
    </div>
  );
}
